import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { createOperationIncidentNote, OperationActivitySummary, OperationIncidentNote } from '@networkRequest';
import { useUser } from '@hooks/useUser';
import IncidentNote from './IncidentNote';
import IncidentActivity from './IncidentActivity';

type FormErrors = {
  description: string | null;
};

const defaultFormErrors = { description: null };

type IncidentNotesProps = {
  incidentId: string;
  incidentActivity: OperationActivitySummary | null;
  activityPagePath: string;
  notes: OperationIncidentNote[];
  onEdit: () => void;
  onDelete: () => void;
  setSelectedItem: Dispatch<SetStateAction<any | null>>;
  onCreate: (params: OperationIncidentNote) => void;
  onError: (error: string) => void;
};

const IncidentNotes: React.FC<IncidentNotesProps> = ({
  incidentId,
  incidentActivity,
  activityPagePath,
  notes,
  onEdit,
  onDelete,
  setSelectedItem,
  onCreate,
  onError
}) => {
  const { user } = useUser();

  const [newNoteDescription, setNewNoteDescription] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);
  const [saving, setSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!saving && inputRef.current !== null) {
      inputRef.current.focus();
    }
  }, [saving]);

  const clearFormData = () => {
    setNewNoteDescription('');
    setFormErrors(defaultFormErrors);
  };

  const validateForm = (description: string) => {
    const isDescriptionValid = description.length > 0;
    setFormErrors({
      description: isDescriptionValid ? null : 'Description is required',
    });

    return isDescriptionValid;
  };

  const handleDescriptionChange = (value: string) => {
    setNewNoteDescription(value);
    validateForm(value);
  };

  const handleSave = async () => {
    if (user === null) {
      return;
    }
    if (!validateForm(newNoteDescription)) {
      return;
    }

    try {
      setSaving(true);
      const params = {
        description: newNoteDescription
      }
      const { data, error } = await createOperationIncidentNote({
        accessToken: user.accessToken,
        incidentId,
        params
      });

      if (data) {
        onCreate(data);
        clearFormData();
        return;
      }

      if (error) {
        onError(error)
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid size={12} sx={{ marginTop: '32px' }}>
      <Box
        sx={{
          marginBottom: '32px',
          paddingLeft: '12px',
          paddingRight: '12px'
        }}
      >
        <Typography component="h2" variant="h6">Notes</Typography>

        <Grid sx={{ marginTop: '16px', marginBottom: '8px' }}>
          <TextField
            inputRef={inputRef}
            disabled={saving}
            required
            multiline
            fullWidth
            label="New Note"
            onChange={(e) => handleDescriptionChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSave();
              }
            }}
            value={newNoteDescription}
            error={formErrors.description !== null}
            sx={{
              padding: 0,
              '& textarea': {
                padding: 0,
              },
              '& .MuiInputBase-input': {
                padding: 0
              }
            }}
          />
        </Grid>
        <Grid container sx={{
          justifyContent: 'flex-end'
        }}>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            Save
          </Button>
        </Grid>
      </Box>

      {incidentActivity !== null &&
        <IncidentActivity
          activity={incidentActivity}
          activityPagePath={activityPagePath}
        />
      }

      {notes.map((note) => {
        return (
          <IncidentNote
            key={note.id}
            note={note}
            onEdit={onEdit}
            onDelete={onDelete}
            setSelectedItem={setSelectedItem}
          />
        )
      })}
    </Grid>
  );
};

export default IncidentNotes;
